"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";

export default function RegisterPage() {
  const router = useRouter();
  const [form, setForm] = useState({
    username: "",
    fullName: "",
    email: "",
    password: "",
    password2: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Validace
    if (!/^[a-zA-Z0-9._-]{3,64}$/.test(form.username)) {
      setError("Uživatelské jméno musí mít 3–64 znaků a může obsahovat jen písmena/číslice a znaky . _ -");
      return;
    }
    if (!form.email.includes("@")) {
      setError("Zadejte prosím platný e-mail.");
      return;
    }
    if (form.password.length < 8) {
      setError("Heslo musí mít alespoň 8 znaků.");
      return;
    }
    if (form.password !== form.password2) {
      setError("Hesla se neshodují.");
      return;
    }

    setLoading(true);
    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    const data = await res.json();
    setLoading(false);

    if (!res.ok) {
      setError(data.error ?? "Chyba při registraci.");
    } else {
      router.push("/login?registered=1");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-[460px] surface-card p-6 shadow-lg">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-6">
          <Image src="/assets/logo.png" alt="Šíma Elektro" width={44} height={44} className="object-contain rounded-2xl" />
          <div>
            <div className="font-bold text-lg tracking-tight">Šíma Elektro</div>
            <div className="text-xs text-muted-foreground">Intranet – Registrace</div>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 rounded-xl bg-destructive/10 text-destructive text-sm font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1.5">Uživatelské jméno</label>
            <input
              type="text"
              autoComplete="username"
              required
              value={form.username}
              onChange={(e) => update("username", e.target.value)}
              placeholder="např. novak.j"
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">Povoleno: písmena/číslice a znaky . _ -</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Jméno a příjmení (volitelné)</label>
            <input
              type="text"
              autoComplete="name"
              value={form.fullName}
              onChange={(e) => update("fullName", e.target.value)}
              placeholder="Jan Novák"
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">E-mail</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="jan.novak@example.com"
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Heslo</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Heslo znovu</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={form.password2}
              onChange={(e) => update("password2", e.target.value)}
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-1 px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Registruji..." : "Registrovat"}
          </button>

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Šíma Elektro</span>
            <a href="/login" className="text-xs font-semibold text-lavender hover:opacity-80 transition-opacity">
              Zpět na přihlášení
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
